import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Logo } from "../assets/icons/Icons";
import { useUserData } from "../lib/contexts/UserContext";
import HospitalCard from "../components/HospitalCard";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import { useNavigate } from "react-router-dom";
import LanguageSelector from "../components/LanguageSelector";
import { getAudioLanguageFromLocalStorage } from "../lib/utils/speechUtils";
import { IoSettingsOutline } from "react-icons/io5";
import { Doctor } from "../lib/utils/types";
import { getProfilesByHospitalId, getProfileByHospitalAndProfileId, Profile } from "../lib/apis/profile";
import { toast } from "react-toastify";
import { setAudioLanguageToLocalStorage } from "../lib/utils/speechUtils";
import { getDoctorListByHospitalId } from "../lib/apis/doctor";


const UserIcon = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
    <path
      d="M16 16C18.7614 16 21 13.7614 21 11C21 8.23858 18.7614 6 16 6C13.2386 6 11 8.23858 11 11C11 13.7614 13.2386 16 16 16Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 26C7 22.134 10.134 19 14 19H18C21.866 19 25 22.134 25 26"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ChooseViewProps {
  setToggleStates: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChooseView: React.FC<ChooseViewProps> = ({ setToggleStates }) => {
  const { handleLogout, accessToken } = useUserData();
  const {
    hospitalID,
    allDoctorsData = [],
    setDoctors,
  } = useHospDocData(); 
  const navigate = useNavigate();

  const [recentSelection, setRecentSelection] = useState<Doctor[] | null>(null);
  const [apiProfiles, setApiProfiles] = useState<Profile[]>([]);
  const [currentProfile, setCurrentProfile] = useState<string>("");
  const sortedDoctors = useMemo(() => {
    if (!allDoctorsData) return [];
    return [...allDoctorsData].sort((a, b) => { 
      // Cast to 'any' to access 'is_active', just like in your commented-out code
      const a_active = (a as any).is_active === true ? 1 : 0;
      const b_active = (b as any).is_active === true ? 1 : 0;
      
      // Sorts active (1) before inactive (0)
      return b_active - a_active;
    });
  }, [allDoctorsData]);

  const [isToggled, setIsToggled] = useState<boolean>(() => {
    return localStorage.getItem("adsToggle") === "true";
  });


  const getSavedProfileId = (hospitalId: string): string | null => {
    try {
      const saved = localStorage.getItem(`selectedProfile_${hospitalId}`);
      return saved || null;
    } catch {
      return null;
    }
  };

  const saveProfileId = (hospitalId: string, profileId: string) => {
    try {
      localStorage.setItem(`selectedProfile_${hospitalId}`, profileId);
    } catch (error) {
      console.error("Error saving profile to localStorage:", error);
    }
  };

  const loadProfiles = useCallback(async (hospitalId: string) => {
    if (!accessToken) return;

    try {
      const response = await getProfilesByHospitalId(hospitalId);
      
      if (response?.status === 200 && (response?.data?.status === "SUCCESS" || response?.data?.status === 1)) {
        const profiles = (response.data.result || response.data.data || []).filter((p: any) => p.is_active !== false);
        
        if (profiles.length > 0) {
          setApiProfiles(profiles);

          const defaultProfile = profiles.find((p: any) => p.is_default === true);
          const defaultId = defaultProfile ? (defaultProfile.id || defaultProfile.tv_config_profile_id || defaultProfile.profile_id) : null;

          // Always check for saved profile first, prioritize it over current state
          const savedProfileId = getSavedProfileId(hospitalId);
          const savedProfile = savedProfileId ? profiles.find((p: any) => {
            const pId = p.id || p.tv_config_profile_id || p.profile_id;
            return pId === savedProfileId;
          }) : null;

          if (savedProfile) {
            const savedId = savedProfile.id || savedProfile.tv_config_profile_id || savedProfile.profile_id;
            if (savedId && savedId !== currentProfile) {
              setCurrentProfile(savedId);
            }
          } else if (!currentProfile) {
            // Only set default/first profile if no saved profile and no current profile
            if (defaultId) {
              setCurrentProfile(defaultId);
            } else if (profiles.length > 0) {
              const firstProfileId = profiles[0].id || profiles[0].tv_config_profile_id || profiles[0].profile_id;
              if (firstProfileId) {
                setCurrentProfile(firstProfileId);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error loading profiles:", error);
    }
  }, [accessToken, currentProfile]);

  useEffect(() => {
    if (hospitalID && accessToken) {
      loadProfiles(hospitalID);
    }
  }, [hospitalID, accessToken, loadProfiles]);

  const navigateToProfileView = async (profileData: Profile) => {
    if (!hospitalID) return;

    if (profileData.language) {
      setAudioLanguageToLocalStorage(profileData.language);
    }

    if (profileData.doctors_list && Array.isArray(profileData.doctors_list) && profileData.doctors_list.length > 0) {
      let doctorsToMatch: Doctor[] = [];
      if (!allDoctorsData || allDoctorsData.length === 0) {
        const docResponse = await getDoctorListByHospitalId(hospitalID);
        if (docResponse?.status === 200 && docResponse?.data?.status === "SUCCESS") {
          doctorsToMatch = docResponse.data.result || [];
        }
      } else {
        doctorsToMatch = allDoctorsData;
      }

      if (doctorsToMatch.length > 0) {
        const doctorIds = profileData.doctors_list.map((doc: any) => {
          if (typeof doc === 'object' && doc !== null) {
            return doc.id || doc.doctor_id || doc.mapping_id || null;
          }
          return typeof doc === 'string' ? doc.trim() : null;
        }).filter(Boolean);

        const selectedDoctors = doctorsToMatch.filter((doc: Doctor) => {
          if (!doc) return false;
          
          return doctorIds.some((profileDoctorId: string) => {
            if (!profileDoctorId) return false;
            
            const normalizedProfileId = String(profileDoctorId).trim().toLowerCase();
            
            if (doc.mapping_id) {
              const normalizedMappingId = String(doc.mapping_id).trim().toLowerCase();
              if (normalizedMappingId === normalizedProfileId) return true;
            }
            
            if (doc.doctor_id) {
              const normalizedDoctorId = String(doc.doctor_id).trim().toLowerCase();
              if (normalizedDoctorId === normalizedProfileId) return true;
            }
            
            if ((doc as any).id) {
              const normalizedId = String((doc as any).id).trim().toLowerCase();
              if (normalizedId === normalizedProfileId) return true;
            }
            
            return false;
          });
        });

        if (selectedDoctors.length > 0) {
          setDoctors(selectedDoctors);
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    }

    const viewType = profileData.view_type;
    if (viewType === "single-doctor" || viewType === "carousel") {
      navigate(`/${hospitalID}/carousel-view`);
    } else if (viewType === "column") {
      navigate(`/${hospitalID}/column-carousel-view`);
    } else {
      navigate(`/${hospitalID}`);
    }
  };

  const handleProfileChange = async (profileId: string) => {
    if (profileId === currentProfile) return;
    
    if (!hospitalID || !accessToken) return;

    const apiProfile = apiProfiles.find((p: any) => {
      const pId = p.id || p.tv_config_profile_id || p.profile_id;
      return pId === profileId;
    });
    
    if (!apiProfile) {
      toast.error("Profile not found");
      return;
    }

    const isDefault = apiProfile.is_default === true;
    const profileName = isDefault ? "Default Profile" : (apiProfile.tv_profile_name || apiProfile.profile_name || `Profile`);

    setCurrentProfile(profileId);
    if (hospitalID) {
      saveProfileId(hospitalID, profileId);
    }

    if (isDefault) {
      navigate(`/${hospitalID}`);
      toast.info(`Switched to ${profileName}`);
      return;
    }

    try {
      const response = await getProfileByHospitalAndProfileId(hospitalID, profileId);
      
      if (response?.status === 200 && (response?.data?.status === 1 || response?.data?.status === "SUCCESS")) {
        const profileData = response.data.data || response.data.result || response.data;
        
        if (profileData) {
          await navigateToProfileView(profileData);
          toast.info(`Switched to ${profileName}`);
        } else {
          const fallbackProfile: Profile = {
            ...apiProfile,
            view_type: apiProfile.view_type || "column",
            language: apiProfile.language || "en",
            doctors_list: apiProfile.doctors_list || [],
          };
          await navigateToProfileView(fallbackProfile);
          toast.info(`Switched to ${profileName}`);
        }
      } else if (response?.status === 403) {
        const fallbackProfile: Profile = {
          ...apiProfile,
          view_type: apiProfile.view_type || "column",
          language: apiProfile.language || "en",
          doctors_list: apiProfile.doctors_list || [],
        };
        await navigateToProfileView(fallbackProfile);
        toast.info(`Switched to ${profileName}`);
      } else {
        const fallbackProfile: Profile = {
          ...apiProfile,
          view_type: apiProfile.view_type || "column",
          language: apiProfile.language || "en",
          doctors_list: apiProfile.doctors_list || [],
        };
        await navigateToProfileView(fallbackProfile);
        toast.info(`Switched to ${profileName}`);
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      const fallbackProfile: Profile = {
        ...apiProfile,
        view_type: apiProfile.view_type || "column",
        language: apiProfile.language || "en",
        doctors_list: apiProfile.doctors_list || [],
      };
      await navigateToProfileView(fallbackProfile);
      toast.info(`Switched to ${profileName}`);
    }
  };

  useEffect(() => {
    try {
      const savedDoctorsJSON = localStorage.getItem('recentColumnViewDoctors');
      if (savedDoctorsJSON) {
        const savedDoctors: Doctor[] = JSON.parse(savedDoctorsJSON);
        if (savedDoctors.length > 0) {
          setRecentSelection(savedDoctors); // Store the full array
        }
      }
    } catch (error) {
      console.error("Could not read recent doctors from storage", error);
    }
  }, []); 
/*
  const uniqueSpecializations = useMemo(() => {
    if (!allDoctorsData) return [];
    const specs = allDoctorsData
      .map(doc => doc.specialization)
      .filter(Boolean); 
    return Array.from(new Set(specs)).sort();
  }, [allDoctorsData]);

  const filteredDoctors = useMemo(() => {
    if (filterType === 'specialization' && selectedSpecialization === 'All') {
      return []; 
    }

    if (selectedSpecialization === 'All') {
      return allDoctorsData;
    }

    return (allDoctorsData||[])
    .sort((a, b) => { 
        const a_active = (a as any).is_active === true ? 1 : 0;
        const b_active = (b as any).is_active === true ? 1 : 0;
        return b_active - a_active; // Sorts active (1) before inactive (0)
      })
    .filter(
      (doctor) => doctor.specialization === selectedSpecialization
    );
  }, [allDoctorsData, selectedSpecialization, filterType]);

  const groupedDoctors = useMemo(() => {
    if (filterType !== 'specialization' || selectedSpecialization !== 'All') {
      return null;
    }
    const groups = new Map<string, Doctor[]>();
    const sortedDoctors = (allDoctorsData || []).sort((a, b) => {
      const a_active = (a as any).is_active === true ? 1 : 0;
      const b_active = (b as any).is_active === true ? 1 : 0;
      return b_active - a_active; // Sorts active (1) before inactive (0)
    });

    // ✅ Change this line to use 'sortedDoctors'
    sortedDoctors.forEach(doctor => {
      const spec = doctor.specialization || 'Uncategorized';
      if (!groups.has(spec)) {
        groups.set(spec, []);
      }
      groups.get(spec)?.push(doctor);
    });
    return groups;
  }, [allDoctorsData, filterType, selectedSpecialization]);
*/

  const handleRecentViewClick = () => {
    if (!recentSelection) return; 
    setDoctors(recentSelection);
    if (recentSelection.length === 1) {
      navigate(`/${hospitalID}/carousel-view`);
    } else if (recentSelection.length > 3) {
      navigate(`/${hospitalID}/column-carousel-view`);
    } else {
      navigate(`/${hospitalID}/column-view`);
    }
  };

  

  useEffect(() => {
    setToggleStates(isToggled);
  }, [isToggled, setToggleStates]);


  const handleToggle = () => {
    const newToggleState = !isToggled;
    setIsToggled(newToggleState);
    setToggleStates(newToggleState);
    localStorage.setItem("adsToggle", newToggleState.toString());
  };

  return (
    <>
      <div className="flex flex-col min-h-screen">
        <div className="flex justify-between items-start p-4 sm:p-6">
        <div className="flex flex-col gap-2">
         <Logo />

          {/*  <div className="flex items-center gap-4 mt-2 ml-10">
            <div className="relative">
              <IoFilter className="w-4 h-4 text-gray-600 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value as any);
                  setSelectedSpecialization("All");
                }}
                className="font-semibold text-gray-700 bg-white border border-gray-300 pl-10 pr-8 py-2 rounded-lg text-sm transition-colors duration-200 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none"
              >

                <option value="default">Filter by: Default</option>
                <option value="specialization">Filter by: Specialization</option>
              </select>
              <IoChevronDownOutline className="w-4 h-4 text-gray-600 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            
            {uniqueSpecializations.length > 0 && (
             <select
               value={selectedSpecialization}
               onChange={(e) => setSelectedSpecialization(e.target.value)}
                className="font-semibold text-[#0F3CBE] bg-transparent border border-[#0F3CBE] px-4 py-2 rounded-lg text-sm transition-colors duration-200 hover:bg-gray-100 focus:outline-none"
              >
               <option value="All">All Specializations</option>
                {uniqueSpecializations.map((spec) => (
                 <option key={spec} value={spec}>
                   {spec}
                   </option>
                ))}
              </select>
            )}
          </div>*/}
          </div>

            <div className="flex flex-wrap items-center justify-end gap-4">
            {apiProfiles.length > 1 && (
              <div className="flex items-center gap-3 sm:gap-4">
                {apiProfiles.map((profile: any, index: number) => {
                  const profileId = profile.id || profile.tv_config_profile_id || profile.profile_id;
                  const isDefault = profile.is_default === true;
                  const isActive = currentProfile === profileId;
                  const bgColor = index % 2 === 0 ? "#0B74FF" : "#053B7D";
                  const profileName = isDefault ? "Default Profile" : (profile.tv_profile_name || profile.profile_name || `Profile ${index + 1}`);
                  
                  return (
                    <button
                      key={profileId}
                      className="flex flex-col items-center focus:outline-none transition-all duration-200"
                      onClick={() => handleProfileChange(profileId)}
                    >
                      <div
                        className="w-10 h-10 sm:w-10 sm:h-10 rounded-lg transition-all duration-200 flex items-center justify-center border-2"
                        style={{
                          backgroundColor: bgColor,
                          borderColor: isActive ? '#2668FF' : 'transparent',
                          boxShadow: isActive ? '0 4px 12px rgba(38,104,255,0.4)' : undefined,
                          transform: isActive ? 'scale(1.05)' : 'scale(1)',
                        }}
                      >
                        <UserIcon color="#FFFFFF" />
                      </div>
                      <p
                        className={`mt-1.5 text-xs sm:text-sm font-medium text-center max-w-[55px] sm:max-w-[55px] truncate ${
                          isActive ? 'text-[#0B74FF]' : 'text-gray-500'
                        }`}
                      >
                        {profileName}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
            {recentSelection && (
                <button
                  className="font-semibold text-[#0F3CBE] bg-transparent border border-[#0F3CBE] px-4 py-2 rounded-lg text-sm transition-colors duration-200 hover:bg-[#0F3CBE] hover:text-white"
                  onClick={handleRecentViewClick}
                >
                  Recent View
                </button>
            )}
            <div className="flex items-center">
            <h1 className="flex items-center mr-2 sm:mr-3 font-semibold text-base sm:text-xl">Ads</h1>
            <label className="relative inline-flex items-center cursor-pointer mr-2 sm:mr-4">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={isToggled}
                onChange={handleToggle}
              />
              <div className="group peer bg-white rounded-full duration-300 w-12 sm:w-14 h-5 sm:h-6 ring-[1.5px] ring-[#A6A6A6] after:duration-300 after:bg-[#A6A6A6] peer-checked:after:bg-lime-500 peer-checked:ring-lime-500 after:rounded-full after:absolute after:h-3 after:w-3 sm:after:h-4 sm:after:w-4 after:top-[3.5px] sm:after:top-[3.5px] after:left-1 after:flex after:justify-center after:items-center peer-checked:after:translate-x-7 sm:peer-checked:after:translate-x-8 peer-hover:after:scale-95" />
            </label>
            </div>
          </div>
        </div>
          <div className="flex-1 flex flex-col">
            <div className="flex-1 flex flex-col px-2 sm:px-4 pt-4 sm:pt-6 gap-4 sm:gap-6 overflow-y-auto mb-20"> 
            {/*  {groupedDoctors ? (

                Array.from(groupedDoctors.entries()).map(([specialization, doctors]) => (
                  <div key={specialization} className="w-full">
                    <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 ml-1">
                      {specialization}
                    </h2>
                    <div className="flex flex-row flex-wrap justify-start items-stretch gap-4 sm:gap-6">
                      {(hospitalID === "fa577fb4-6353-44ae-9a41-0d362d0ab5ce"
                        ? doctors.slice(0, 2)
                        : doctors
                      ).map((doctor) => (
                        <HospitalCard
                          key={doctor.doctor_id}
                          logo={doctor.profile_picture}
                          name={doctor.full_name}
                          gender={doctor.gender}
                          qualification={doctor.qualification}
                          specialization={doctor.specialization}
                          is_active={(doctor as any).is_active}
                          docCount={"none"}
                          show_name_prefix={doctor.show_name_prefix !== false}
                          handlePress={() => {
                            const selection = [doctor];
                            setDoctors(selection); 
                            try {
                             localStorage.setItem('recentColumnViewDoctors', JSON.stringify(selection));
                            } catch (error) {
                              console.error("Failed to save single doctor selection:", error);
                            }
                            navigate("/" + hospitalID + "/carousel-view");
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  ))
               ) : ( */}
            <div className="flex flex-row flex-wrap justify-center items-stretch gap-4 sm:gap-6">
              {hospitalID === "fa577fb4-6353-44ae-9a41-0d362d0ab5ce"
                ? sortedDoctors?.slice(0, 2).map((doctor) => (
                    <HospitalCard
                      key={doctor.doctor_id}
                      logo={doctor.profile_picture}
                      name={doctor.full_name}
                      gender={doctor.gender}
                      qualification={doctor.qualification}
                      specialization={doctor.specialization}
                      is_active={(doctor as any).is_active}
                      docCount={"none"}
                      hide_from_tv={doctor.hide_from_tv}
                      show_name_prefix={doctor.show_name_prefix !== false}
                      handlePress={() => {
                        const selection = [doctor];
                        setDoctors(selection); 
                        try {
                          localStorage.setItem('recentColumnViewDoctors', JSON.stringify(selection));
                        } catch (error) {
                          console.error("Failed to save single doctor selection:", error);
                        }
                        navigate("/" + hospitalID + "/carousel-view");
                      }}
                    />
                  ))
                : sortedDoctors?.map((doctor) => (
                    <HospitalCard
                      key={doctor.doctor_id}
                      logo={doctor.profile_picture}
                      name={doctor.full_name}
                      gender={doctor.gender}
                      qualification={doctor.qualification}
                      specialization={doctor.specialization}
                      is_active={(doctor as any).is_active}
                      docCount={"none"}
                      hide_from_tv={doctor.hide_from_tv}
                      show_name_prefix={doctor.show_name_prefix !== false}
                      handlePress={() => {
                        const selection = [doctor];
                        setDoctors(selection); 
                        try {
                          localStorage.setItem('recentColumnViewDoctors', JSON.stringify(selection));
                        } catch (error) {
                          console.error("Failed to save single doctor selection:", error);
                        }
              
                        navigate("/" + hospitalID + "/carousel-view");
                      }}
                    />
                  ))}
            </div>
            {/*}   )} */}
            </div>
            <div className="fixed bottom-0 left-0 right-0 flex px-4 sm:px-8 py-4 sm:py-4 items-center justify-between bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
              <LanguageSelector
                selectedLanguage={getAudioLanguageFromLocalStorage()}
                onLanguageChange={() => {}}
              />
              <div className="flex flex-row gap-4 sm:gap-8">
                {/* 1. Settings */}
                <div
                  className="flex flex-col items-center justify-center cursor-pointer"
                  onClick={() => navigate("/" + hospitalID + "/settings")}
                >
                  <div className="flex items-center justify-center bg-[#0F3CBE] rounded-full h-10 w-10 sm:h-14 sm:w-14">
                    <IoSettingsOutline className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-gray-700 mt-1.5">
                    Settings
                  </p>
                </div>
                {/* 2. Slideshow View */}
                <div
                  className="flex flex-col items-center justify-center cursor-pointer"
                  onClick={() => {
                    //let doctorsForSlideshow = filteredDoctors;
                  //if (groupedDoctors) {
                    //doctorsForSlideshow = Array.from(groupedDoctors.values()).flat();
                  //}
                    setDoctors(
                      hospitalID === "fa577fb4-6353-44ae-9a41-0d362d0ab5ce"
                        ? allDoctorsData?.slice(0, 2)
                        : allDoctorsData
                    );
                    navigate("/" + hospitalID + "/choose-carousel-doctors");
                  }}
                >
                  <div className="flex items-center justify-center bg-[#0F3CBE] rounded-full h-10 w-10 sm:h-14 sm:w-14">
                    <img
                      src="https://ik.imagekit.io/gn9by35ed/Frame1.png?updatedAt=1738340649342"
                      className="w-6 h-6 sm:w-8 sm:h-8"
                      alt="Slideshow View"
                    />
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-gray-700 mt-1.5">
                    Slideshow
                  </p>
                </div>
                {/* 3. Column View */}
                <div
                  className="flex flex-col items-center justify-center cursor-pointer"
                  onClick={() => navigate("/" + hospitalID + "/choose-doctors")}
                >
                  <div className="flex items-center justify-center bg-[#0F3CBE] rounded-full h-10 w-10 sm:h-14 sm:w-14">
                    <img
                      src="https://ik.imagekit.io/gn9by35ed/Frame%202.png?updatedAt=1738340649063"
                      className="w-6 h-6 sm:w-8 sm:h-8"
                      alt="Column View"
                    />
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-gray-700 mt-1.5">
                    Columns
                  </p>
                </div>
                {/* 4. Log Out */}
                <div
                  className="flex flex-col items-center justify-center cursor-pointer"
                  onClick={handleLogout}
                >
                  <div className="flex items-center justify-center bg-[#F32F2F] rounded-full h-10 w-10 sm:h-14 sm:w-14">
                    <img
                      src="https://ik.imagekit.io/gn9by35ed/Frame3.png?updatedAt=1738340649328"
                      className="w-6 h-6 sm:w-8 sm:h-8"
                      alt="Log Out"
                    />
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-gray-700 mt-1.5">
                    Log Out
                  </p>
                </div>
              </div>
              </div>
            </div>
          </div>
      </>
    );
  };

export default ChooseView;
